import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { reduxForm } from 'redux-form';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import ToggleSwitch from '@tcp/core/src/components/common/atoms/ToggleSwitch/views/ToggleSwitch';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import { getLocalStorage } from '@tcp/core/src/utils/localStorageManagement';
import { isCanada } from '@tcp/core/src/utils/utils';
import { getNextOpenDay } from '@tcp/core/src/utils/getNextDayDate.util';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
  getDynamicLabelValues,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { BopisFilterLabels } from '@tcp/core/src/components/features/browse/ProductListing/molecules/BopisFilter/views/BopisFilter.labels';
import { labelsObject } from '@tcp/core/src/components/common/organisms/PickupStoreModal/molecules/PickupStoreSelectionForm/views/PickupStoreSelectionForm.labels.js';
import createPayload from '@tcp/core/src/components/features/browse/ProductListing/molecules/utils/filterAnalytics';
import OneHrFilterWrapper, {
  OneHrFilterTextArea,
  DefaultStoreMessage,
  PickupLabel,
} from '../styles/OneHrFilter.styles';
import { getParams, getUpdatedUrl } from '../utils/OneHrFilter.utils';
import ChangeStoreLink from '../../../../common/atoms/ChangeStoreLink/views/ChangeStoreLink.view';
import useStoreData from '../hooks/useStoreData';
import useStoreText from '../hooks/useStoreText';
import {
  getCurrentFeatureBrandAttribute,
  getFeatureBrand,
} from '../../SearchDetail/container/SearchDetail.util';

const StoreLocatorModal = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "stor-locator-modal" */
      '@tcp/core/src/components/common/organisms/StoreLocatorModal'
    ),
  { ssr: false }
);

const OneHrFilter = ({
  defaultStore,
  isBopisFilterOn,
  isOneHrFilterEnabled,
  updateBopisFormValue,
  setBopisFilterStateActn,
  formValues = {},
  setUpdatedUrlActn,
  pathName,
  isFacetedNavigationEnabled,
  seoFacetedNavigationConfig,
  isCleanUrlForSizeEnabled,
  filterMaps,
  trackClickAnalyticsBopisFilter,
  onStoreChangeLinkClick,
  resetSuggestedStores,
  trackAnalyticsClick,
  onSubmit,
  getProducts,
  isSLP,
  isFullDateDisplayEnabled,
  storeHoursThreshold,
  isDisplayPickupTimeEnabled,
  shouldAllowOnlineProductPickup,
  isFeatureBrandView,
  isPLPSortAndOneHrFilterRedesignEnabled,
  isBopisEmptyResults,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const storeData = useStoreData(defaultStore);
  const [modalStoreSearchData, setModalStoreSearchData] = useState(null);
  const [bopisStoreSelectorModalOpen, setBopisStoreSelectorModalOpen] = useState(false);

  const bopisFilterLabels = getLabelFromCopyOrFromTranslation('PLP.plpTiles', BopisFilterLabels);

  const { storeChangeText, storeSelectionMessage } = useStoreText(storeData, bopisFilterLabels);

  const isStoreAvailable = storeData?.basicInfo?.id;

  const isStorePermanentClosed = storeData?.hours?.regularHours[0]?.isClosed === true;

  const isPlp = pathName?.includes('/c');

  let clothingSizeMap = [];

  if (isCleanUrlForSizeEnabled) {
    clothingSizeMap = filterMaps?.v_clothingsize_uFilter;
  }

  // Apply BOPIS filter logic - it will append the filters in the URL and change URL action will get the products based on the applied filters
  const submitFilterChange = (formData = {}) => {
    const formDetails = formData;
    const { asPath, filtersFromUrl } = getParams(
      pathName,
      formDetails,
      isFacetedNavigationEnabled,
      false,
      seoFacetedNavigationConfig,
      {
        isCleanUrlForSizeEnabled,
        clothingSizeMap,
      }
    );
    const updatedUrl = getUpdatedUrl(
      false,
      filtersFromUrl,
      asPath,
      isFacetedNavigationEnabled,
      seoFacetedNavigationConfig,
      {
        isCleanUrlForSizeEnabled,
        clothingSizeMap,
      }
    );
    setUpdatedUrlActn({ updatedUrl });
  };

  const toggleBopisFilter = () => {
    let tcpWebOnlyBopisFilter = isCanada()
      ? '-TCPWebOnlyFlagCanadaStore:1'
      : '-TCPWebOnlyFlagUSStore:1';

    if (shouldAllowOnlineProductPickup) {
      tcpWebOnlyBopisFilter = '';
    }
    const storeFilterId = [
      `v_Quantity_${storeData?.basicInfo?.id?.slice(-4)}:[1 TO *]${tcpWebOnlyBopisFilter}`,
    ];
    let updatedFormValues = {};
    if (isBopisFilterOn) {
      updateBopisFormValue('bopisStoreId', []);
      updatedFormValues = { ...formValues, bopisStoreId: [] };
    } else {
      updateBopisFormValue('bopisStoreId', storeFilterId);
      updatedFormValues = { ...formValues, bopisStoreId: storeFilterId };
      // Trigger analytics.
      if (formValues?.sort?.length > 0) {
        trackClickAnalyticsBopisFilter({
          bopis_cd50: createPayload(updatedFormValues),
          customEvents: ['eventb50'],
        });
      } else {
        trackClickAnalyticsBopisFilter({
          bopis_cd51: createPayload(updatedFormValues),
          customEvents: ['eventb51'],
        });
      }
    }
    setBopisFilterStateActn(!isBopisFilterOn);
    if (isFeatureBrandView) {
      updatedFormValues = {
        ...updatedFormValues,
        [getCurrentFeatureBrandAttribute(filterMaps)]: [getFeatureBrand()],
      };
    }
    if (getProducts) {
      onSubmit(updatedFormValues, false, getProducts);
    } else {
      submitFilterChange(updatedFormValues);
    }
  };

  // Handlers - 1. Toggle Bopis Filter.
  const isStorePresentForTogglingFunc = () => {
    setTimeout(() => {
      const store = JSON.parse(getLocalStorage('defaultStore'));
      if (store) {
        toggleBopisFilter();
      }
    }, 0);
  };

  const isStorePresentForToggling = () => {
    setTimeout(() => {
      isStorePresentForTogglingFunc();
    }, 0);
  };
  // Change store logic goes here -
  const onStoreChange = (store) => {
    const storeFilterId = [`v_Quantity_${store?.basicInfo?.id?.slice(-4)}:[1 TO *]`];
    let updatedFormValues = {};
    if (isBopisFilterOn) {
      updateBopisFormValue('bopisStoreId', storeFilterId);
      updatedFormValues = { ...formValues, bopisStoreId: storeFilterId };
      if (getProducts) {
        onSubmit(updatedFormValues, false, getProducts);
      } else {
        submitFilterChange(updatedFormValues);
      }
    }
  };
  const handleChangeStoreOnKeyPress = (e) => {
    if (e.keyCode !== 32) return;
    e.preventDefault();
    onStoreChangeLinkClick();
  };

  const handleChangeStoreLinkClickFunc = () => {
    trackClickAnalyticsBopisFilter({ bopis_filter_metrics168: 1, customEvents: ['event168'] });
    if (onStoreChangeLinkClick) {
      onStoreChangeLinkClick();
    }
    resetSuggestedStores([]);
    setBopisStoreSelectorModalOpen(true);
    setModalStoreSearchData(storeData);
  };

  const handleChangeStoreLinkClick = () => {
    setTimeout(() => {
      handleChangeStoreLinkClickFunc();
    }, 0);
  };

  const handleBopisStoreModalCloseFunc = () => {
    setBopisStoreSelectorModalOpen(false);
    setModalStoreSearchData(null);
  };
  const handleBopisStoreModalClose = () => {
    setTimeout(() => {
      handleBopisStoreModalCloseFunc();
    }, 0);
  };

  const generateMessage = () => {
    if (!isDisplayPickupTimeEnabled)
      return <DefaultStoreMessage>{storeSelectionMessage}</DefaultStoreMessage>;

    const dayDetails = getNextOpenDay({
      hoursBeforeClose: storeHoursThreshold,
    });
    const { dayName, openDate } = dayDetails || {};
    const freePickUpLbl = getLabelFromCopyOrFromTranslation(
      'Browse.ProductPickup.lbl_Product_pickup_Free_Pick_Up',
      labelsObject
    );

    const freePickUpTodayLbl = renderLabel(
      getDynamicLabelValues('dynamic.browse_d.lbl_Product_pickup_Free_Pickup_Up_Today'),
      'lbl_Product_pickup_Free_Pickup_Up_Today'
    );

    if (dayName === 'Today') {
      return <PickupLabel>{freePickUpTodayLbl}</PickupLabel>;
    }
    return (
      <>
        <PickupLabel>{`${renderLabel(freePickUpLbl, 'lbl_Product_pickup_Free_Pick_Up')} ${dayName}${
          isFullDateDisplayEnabled && openDate ? `, ${openDate}` : ''
        }`}</PickupLabel>
      </>
    );
  };

  if (
    isEmpty(storeData) ||
    isStorePermanentClosed === true ||
    !isOneHrFilterEnabled ||
    (!isSLP && !isPlp)
  )
    return null;

  return (
    <OneHrFilterWrapper
      id="one-hr-filter"
      className={`${isPLPSortAndOneHrFilterRedesignEnabled ? 'one-hr-filter-redesign' : ''}`}
      isBopisEmptyResults={isBopisEmptyResults}
    >
      <ToggleSwitch
        id="bopis-filter"
        name="bopisFilterSwitch"
        checked={isBopisFilterOn}
        switchColor="#005D95"
        offColor="#C9C9C9"
        toggleSwitchSwitchColor="#fff"
        optionLabels={['', '']}
        onChange={isStorePresentForToggling}
        disabled={!isStoreAvailable && !isEqual(isStoreAvailable, undefined)}
        className={`${
          isPLPSortAndOneHrFilterRedesignEnabled ? 'one-hr-filter-toggle-switch-redesign' : ''
        }`}
      />
      <OneHrFilterTextArea className="one-hr-filter-text-area">
        <BodyCopy
          fontSize={['fs16']}
          fontFamily="secondary"
          className="one-hr-filter-text-area-label"
        >
          {generateMessage()}
        </BodyCopy>
        <ChangeStoreLink
          storeChangeText={storeChangeText}
          handleChangeStoreLinkClick={() => handleChangeStoreLinkClick()}
          handleChangeStoreOnKeyPress={handleChangeStoreOnKeyPress}
          bopisFilterLabels={bopisFilterLabels}
        />
      </OneHrFilterTextArea>
      {bopisStoreSelectorModalOpen && (
        <StoreLocatorModal
          isOpen={bopisStoreSelectorModalOpen}
          onClose={handleBopisStoreModalClose}
          onStoreChange={onStoreChange}
          modalStoreSearchData={modalStoreSearchData}
          trackClickAnalyticsBopisFilter={trackClickAnalyticsBopisFilter}
          trackAnalyticsClick={trackAnalyticsClick}
        />
      )}
    </OneHrFilterWrapper>
  );
};

export default reduxForm({
  form: 'filter-form', // a unique identifier for this form
  enableReinitialize: true,
})(OneHrFilter);
